/* eslint-disable react-hooks/exhaustive-deps */
///<amd-module name="Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/MessageList" />
import * as React from "react";
import { handleError } from "Core/Medius.Core.Web/Scripts/lib/errorHandling/errorHandler";
import { AiThinkingIcon } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/AiThinkingIcon";
import { ChatMessage } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/ChatMessage";
import { WelcomeWidget } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/widgets/WelcomeWidget";
import { isCopilotPredictedNextApproversEnabled } from "Core/Medius.Core.Web/Scripts/Medius/core/featureToggle";
import { focusOnElement, messageHasAnythingToDisplay, scrollToBottom } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/utils";
import * as services from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/services/agentServices";
import { LoaderIcon } from "@medius/ui-controls";
import { useCopilotContext } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/hooks/useCopilotContext";
import * as workflownames from "Core/Medius.Core.Web/Scripts/Medius/workflownames";
import { IMessage, Role } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/agentInterfaces";
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";

export interface IMessageListProps{
    promptInputRef: React.MutableRefObject<any>;
    retryCallback: () => void;
    sendFreetextQuestion: (freetextQuestion: string, isFollowupQuestion: boolean) => Promise<void>
}

export function MessageList({ promptInputRef, retryCallback, sendFreetextQuestion }: IMessageListProps){
    const { 
        isOperational,
        setMessages,
        messages,
        documentId,
        workflowStepName,
        invoiceType,
        setProcessedMessage,
        isStreamActive,
        setIsAgentAvailable,
        setIsLoadingMessages,
        isLoadingMessages,
        processedMessage,
        isDocumentImport
    } = useCopilotContext();
    
    const [predictedNextApprovers, setPredictedNextApprovers] = React.useState<string[]>();
    const [werePredictedNextApproversUpdated, setWerePredictedNextApproversUpdated] = React.useState(false);
    const [isPredictedNextApproversError, setIsPredictedNextApproversError] = React.useState(false);
    const lastMessageRef = React.useRef(null);

    const getPredictedNextApprovers = async () => {
        try {
            if(isCopilotPredictedNextApproversEnabled() && (
                invoiceType === "Medius.ExpenseInvoice.Entities.ExpenseInvoice" && workflowStepName === workflownames.ApproveDocument ||
                invoiceType === "Medius.OrderbasedInvoice.Entities.OrderbasedInvoice" && workflowStepName === workflownames.AnalyzeDocument ||
                invoiceType === "Medius.OrderbasedInvoice.Entities.OrderbasedInvoice" && workflowStepName === workflownames.AuthorizeDeviations ||
                invoiceType === "Medius.OrderbasedInvoice.Entities.OrderbasedInvoice" && workflowStepName === workflownames.AuthorizeInvoiceAmount
            )){
                const data = await services.getPredictedNextApprovers(documentId, invoiceType, workflowStepName);
                setPredictedNextApprovers(data || []);
            } else {
                setPredictedNextApprovers([]);
            }
        }
        catch(error) {
            setIsPredictedNextApproversError(true);
        }
    };

    const finishProcessingMessage = () => {
        if(messageHasAnythingToDisplay(processedMessage)) {
            setMessages(prevMessages => [...prevMessages, processedMessage]);
        }
        setProcessedMessage(null);
    };

    const updateInvoiceContextWithPredictedNextApprovers = () => {
        setWerePredictedNextApproversUpdated(true);
        const invoiceInsightsMessageIndex = messages.findIndex(message => message.operationalInvoiceContextDto);
        if(invoiceInsightsMessageIndex === -1) {
            return;
        }
        setMessages(messages.map((message, index) => {
            if(invoiceInsightsMessageIndex == index) {
                return {
                    ...message, 
                    operationalInvoiceContextDto: {
                        ...message.operationalInvoiceContextDto, 
                        predictedNextApprovers: predictedNextApprovers
                    }
                };
            }
            return message;
        }));
    };

    const shouldDisplayFollowupQuestions = (message: IMessage, index: number) => {
        if(message.role !== Role.assistant)
            return false;
        if(messages.length === 1)
            return false;
        if(index !== messages.length - 1)
            return false;
        if(
            message.operationalInvoiceContextDto || 
            message.isError || 
            message.nonOperationalInvoiceContextDto ||
            message.supplierWidgetDto ||
            message.content === undefined
        ) return false;

        return true;
    };
    
    React.useEffect(() => {
        const getAllMessages = async () => {
            try {
                const previousMessages = await services.getMessages();
          
                if (isOperational) {
                    getPredictedNextApprovers();
                }

                if(!isDocumentImport) {
                    const invoiceContextMessage = await services.getInvoiceContext(documentId, isOperational);
                    if (invoiceContextMessage) {
                        previousMessages.push(invoiceContextMessage);
                    }
                }
                setMessages(previousMessages);
            }
            catch (error) {
                handleError(error);
                setIsAgentAvailable(false);
            }
            finally {
                setIsLoadingMessages(false);                
            }
        };

        if (messages.length === 0) {
            getAllMessages();
        }
    }, []);

    React.useEffect(() => {
        if (processedMessage) {
            if(isStreamActive) {
                scrollToBottom(lastMessageRef);
                return;
            }
            finishProcessingMessage();
        }
    }, [processedMessage, isStreamActive]);

    React.useEffect(() => {
        scrollToBottom(lastMessageRef);
        focusOnElement(promptInputRef);
    }, [messages]);

    React.useEffect(() => {
        if(!werePredictedNextApproversUpdated && (predictedNextApprovers || isPredictedNextApproversError) && messages.length > 0) {
            updateInvoiceContextWithPredictedNextApprovers(); 
        }
    }, [predictedNextApprovers, messages, werePredictedNextApproversUpdated, isPredictedNextApproversError]);
    
    const hasSupportedAttachments = messages[0]?.operationalInvoiceContextDto
        ? messages[0]?.operationalInvoiceContextDto.hasSupportedAttachments 
        : (messages[0]?.nonOperationalInvoiceContextDto ? messages[0]?.nonOperationalInvoiceContextDto.hasSupportedAttachments : false);

    return (
        <div className="mediusAgent__messagesContainer" data-testid="mediusAgent__messagesContainer">{ isLoadingMessages ? (
            <div className="mediusAgent__innerContainer__loaderIconContainer">
                <LoaderIcon />
            </div> ) : ( <>
            <WelcomeWidget/>
            {messages.map((el, index) => (
                <ChatMessage
                    message={el} 
                    key={index} 
                    invoiceType={invoiceType} 
                    workflowStepName={workflowStepName}
                    retryCallback={retryCallback}
                    showRetryButton={index === messages.length-1}
                    isPredictedNextApproversError={isPredictedNextApproversError}
                    shouldDisplayFollowupQuestions={shouldDisplayFollowupQuestions(el, index)}
                    sendFreetextQuestion={sendFreetextQuestion}
                    hasSupportedAttachments={hasSupportedAttachments}
                />
            ))}
            { isStreamActive && !processedMessage?.content && 
                <>
                <AiThinkingIcon />
                {
                    processedMessage?.analyzingImages && 
                    <div className="mediusAgent__analyzingImagesLabel" data-testid="copilotAnalyzingImagesLabel">
                        {translate("#Core/apaCopilotAnalyzingImages")}
                    </div>
                }
                </>
            }
            { messageHasAnythingToDisplay(processedMessage) &&
                <ChatMessage
                    message={processedMessage} 
                    key={messages.length} 
                    invoiceType={invoiceType} 
                    workflowStepName={workflowStepName}
                    retryCallback={retryCallback}
                    showRetryButton={false}
                    isPredictedNextApproversError={isPredictedNextApproversError}
                    shouldDisplayFollowupQuestions={false}
                    sendFreetextQuestion={sendFreetextQuestion}
                    hasSupportedAttachments={hasSupportedAttachments}
                />
            }
            <div className="mediusAgent__lastMessageAnchor" ref={lastMessageRef} /></>)}
        </div>
    );
}